import type { ThankYouConfig } from '@cod/config-schema';

/**
 * The Settings page's thank-you rules.
 *
 * Separate from `Settings.tsx` because that module pulls in Nimbus and the Nexo-backed api client,
 * which need a `window` the node test environment does not have — same split as
 * `shippingRates.model.ts` and `quantityOffers.model.ts`.
 */

/** The control a problem belongs to, so the page can put the message on the offending field. */
export type ThankYouField = 'redirect_url' | 'whatsapp_phone';

export interface ThankYouProblem {
  field: ThankYouField;
  /** i18n key — the model never holds merchant-facing copy, the page translates it. */
  message: string;
}

/** wa.me takes digits only, and E.164 numbers run 8-15 of them once the country code is in. */
const WA_MIN_DIGITS = 8;
const WA_MAX_DIGITS = 15;

/**
 * Whether a redirect target will actually navigate somewhere.
 *
 * Two shapes are accepted, and everything else is not:
 *
 *  - an absolute `http(s)` URL, which is what the handoff button is for;
 *  - a path starting with `/`, which resolves against the store the buyer is already on — a
 *    merchant linking their own `/gracias` page is a real, working config on both bundles.
 *
 * 🚨 A bare domain (`tienda.com`) is the case this exists for. It looks like an address and is
 * not one: the browser resolves it RELATIVE to the product page, so the button lands the buyer on
 * `https://store.com/productos/tienda.com`. `new URL()` is the parser rather than a regex of our
 * own — the platform already knows what a URL is.
 */
function isReachableUrl(raw: string): boolean {
  if (raw.startsWith('/')) return true;
  try {
    const { protocol } = new URL(raw);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
}

/**
 * The problem that must be fixed before this thank-you config can be saved, or null.
 *
 * 🚨 **A mode whose target is empty is a handoff the buyer never sees.** Both renderers
 * (`form-renderer`'s `showThankYou`, `form-tree`'s `thankYouTree`) gate the button on
 * `redirect_url.trim()` / `whatsapp_phone.trim()` and fall through to the merchant's page HTML —
 * or, failing that, to the generic confirmation panel — when it is blank. That tolerance is
 * correct for configs that already exist, and it is why this check belongs HERE: a merchant who
 * deliberately picked "button to a URL" got no button at all, silently, with nothing on either
 * screen to tell them why.
 *
 * Only ever one problem: the mode picks exactly one target, and the other control is not on
 * screen to carry a message.
 *
 * Scoped to `enabled`, because a switched-off thank-you page renders nothing at all — refusing to
 * save a section the merchant has just turned off would trap them in it.
 */
export function validateThankYou(draft: ThankYouConfig): ThankYouProblem | null {
  if (!draft.enabled) return null;
  if (draft.mode === 'url') {
    const url = draft.redirect_url.trim();
    if (!url) return { field: 'redirect_url', message: 'settings.thankYouUrlRequired' };
    if (!isReachableUrl(url)) {
      return { field: 'redirect_url', message: 'settings.thankYouUrlInvalid' };
    }
    return null;
  }
  if (draft.mode === 'whatsapp') {
    // Both renderers build the href from the digits alone (`phone.replace(/\D/g, '')`), so a
    // number of punctuation is exactly as broken as an empty field — `https://wa.me/?text=…`.
    const digits = draft.whatsapp_phone.replace(/\D/g, '');
    if (!digits) return { field: 'whatsapp_phone', message: 'settings.thankYouPhoneRequired' };
    if (digits.length < WA_MIN_DIGITS || digits.length > WA_MAX_DIGITS) {
      return { field: 'whatsapp_phone', message: 'settings.thankYouPhoneInvalid' };
    }
    return null;
  }
  return null;
}
